import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut
} from '@ps20-frontend/shop-integration-redux';
import {
  EinzelleistungDruck,
  Feature,
  SpartenType,
  TkvV2Druckdaten,
  TkvV2VersichertesTierDruck,
  ZusatzoptionenDruck
} from '@ps20/bff-models';
import {
  App,
  LeistungEigenschaft,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
  Produktlinie,
  TierartEnum
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { getProduktlinienDruck, mapGefahren } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { Versicherungsnehmer } from '../reducers/versicherungsnehmer.reducer';
import { IAppState } from '../appState';
import {
  selectSelectedProduktlinie,
  selectShouldDisplayBadgeInclusive,
  selectZusatzoptionItems
} from '../selectors/produkt.selector';
import { selectShouldShowIllnessSection } from '../selectors/vertragsoptionen.selector';
import { convertAnimalTypeToTierart, getBreedValueOrLabel } from './transformer/tkvData.transformer';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.TKV_V2));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.TKV_V2)) {
      yield cancel();
      return;
    }

    const tkvDruckdaten: TkvV2Druckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(tkvDruckdaten));
  } catch (e) {
    PS20Logger.error(App.TKV_V2, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): TkvV2Druckdaten {
  const ausgewaehlteProduktlinie: Produktlinie = selectSelectedProduktlinie(state);
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = !selectShouldDisplayBadgeInclusive(state)
    ? mapZusatzoptionenDruck(selectZusatzoptionItems(state))
    : [];
  const anzahlOperationen = 0;
  const heilbehandlungenVorhanden = false;
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    zusatzoptionenDruck,
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    ablauf: null,
    abweichungVomZielmarkt: false,
    beginn: null,
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.TKV_V2,
      ),
    ),
    versichertesTier: getVersichertesTierDataFromStrecke(state.versicherungsnehmer),
    leistungen: mapLeistungenForTkv(ausgewaehlteProduktlinie),
    produktlinien: getProduktlinienDruck(state.produkt),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    spartenType: SpartenType.TIERKRANKEN_V2,
    zaehlstuecke: state.rabattstufe,
    zuersGefaehrdungsklasse: '',
    treuebonus: state.taasInfo.tbNachlass,
    gefahrenDruck: mapGefahren(state.produkt),
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorschaedenDruck: {
      vorhanden: anzahlOperationen > 0 || heilbehandlungenVorhanden,
      tkvOperation: anzahlOperationen,
      tkvHeilkosten: heilbehandlungenVorhanden ? 999 : 0,
    },
    sublimit: 5000,
    beibehalten: state.vertragsoptionen.beibehalten,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    zuschlag: state.taasInfo.zuschlagRisiko,
    gesundfragenTkvSichtbar: selectShouldShowIllnessSection(state),
  };
}

function mapLeistungenForTkv(ausgewaehlteProduktlinie: Produktlinie): Array<EinzelleistungDruck> {
  return mapLeistungen(
    {
      ...ausgewaehlteProduktlinie,
      leistungen: ausgewaehlteProduktlinie.leistungen.filter(
        leistung => leistung.eigenschaft !== LeistungEigenschaft.EXKLUSIVE,
      ),
    },
    SpartenType.TIERKRANKEN_V2,
  );
}

function getVersichertesTierDataFromStrecke(
  versicherungsnehmer: Versicherungsnehmer,
): TkvV2VersichertesTierDruck {
  if (versicherungsnehmer.animalType === TierartEnum.KATZE) {
    return {
      isKastriert: false,
      geburtsdatum: dayjs(versicherungsnehmer.animalBirthDate, DATEFORMAT_LOCAL_STATE).format(
        DATEFORMAT_SHORT_ISO,
      ),
      geschlecht: versicherungsnehmer.animalGender,
      tierhaltung: versicherungsnehmer.catKeepingType,
      tierart: convertAnimalTypeToTierart(versicherungsnehmer.animalType),
      rasse: versicherungsnehmer.breed?.value,
      hasHeilbehandlungen: versicherungsnehmer.hasIllnesses || false,
      tiername: versicherungsnehmer.animalName,
      kennzeichnungsArt: null,
      kennzeichnung: null,
      istBeiTierarzt: null,
      einsatzgebiet: null,
    };
  }
  return {
    isKastriert: false,
    geburtsdatum: dayjs(versicherungsnehmer.animalBirthDate, DATEFORMAT_LOCAL_STATE).format(
      DATEFORMAT_SHORT_ISO,
    ),
    geschlecht: versicherungsnehmer.animalGender,
    tierart: convertAnimalTypeToTierart(versicherungsnehmer.animalType),
    rasse: getBreedValueOrLabel(versicherungsnehmer, 'value'),
    hasHeilbehandlungen: versicherungsnehmer.hasIllnesses || false,
    tiername: versicherungsnehmer.animalName,
    kennzeichnungsArt: null,
    kennzeichnung: null,
    istBeiTierarzt: null,
    einsatzgebiet: null,
  };
}
